import type { Permission } from '@hp/domain';
import type { PipelineNextAction, PipelineNextActionKind, PipelineStage, PipelineStageKey } from './queries';

/**
 * Stage titles, per-stage button labels and re-run labels live in the `pipeline` catalog
 * (`stages.<key>`, `stageActions.<key>`, `rerun.<key>`); components translate them.
 */

/** Which stage the next action points at — that step gets the highlight. */
export const KIND_TO_STAGE: Partial<Record<PipelineNextActionKind, PipelineStageKey>> = {
  start_sync: 'sync',
  start_delta: 'sync',
  start_backup: 'backup',
  run_enrichment: 'enrich',
  review_enrichment: 'enrich',
  create_publish_dry: 'publish',
  create_publish_live: 'publish',
  run_dedup: 'dedup',
  review: 'review',
  create_dry_run: 'dry_run',
  inspect_dry_run: 'dry_run',
  create_pilot: 'pilot',
  create_full: 'full_merge',
};

/** The permission an action needs; navigation-only actions (open the queue, inspect a dry run) need none. */
export const KIND_PERMISSION: Partial<Record<PipelineNextActionKind, Permission>> = {
  start_sync: 'crm.run_jobs',
  start_delta: 'crm.run_jobs',
  start_backup: 'crm.run_jobs',
  run_enrichment: 'crm.run_jobs',
  run_dedup: 'crm.run_jobs',
  create_publish_dry: 'crm.publish',
  create_publish_live: 'crm.publish',
  create_dry_run: 'crm.merge',
  create_pilot: 'crm.merge',
  create_full: 'crm.merge',
};

export function isNextStage(action: PipelineNextAction, stage: PipelineStage): boolean {
  const key = KIND_TO_STAGE[action.kind];
  if (key === undefined || key !== stage.key) return false;
  return (action.entityType ?? null) === (stage.entityType ?? null);
}

export const STAGE_TO_KIND: Record<PipelineStageKey, PipelineNextActionKind> = {
  sync: 'start_sync',
  backup: 'start_backup',
  enrich: 'run_enrichment',
  publish: 'create_publish_dry',
  dedup: 'run_dedup',
  review: 'review',
  dry_run: 'create_dry_run',
  pilot: 'create_pilot',
  full_merge: 'create_full',
};

/** Every READY stage is directly startable — the banner is guidance, not a gate. */
/** Stages where re-running after "done" is meaningful AND safe (idempotent, no Pipedrive writes). */
export const RERUNNABLE_STAGES = ['sync', 'backup', 'enrich', 'dedup'] as const satisfies readonly PipelineStageKey[];
export type RerunnableStage = (typeof RERUNNABLE_STAGES)[number];

export function isRerunnable(key: PipelineStageKey): key is RerunnableStage {
  return (RERUNNABLE_STAGES as readonly PipelineStageKey[]).includes(key);
}
